import axios from 'axios'

export const API_BASE = 'https://winelands.stratitude-develop.co.za/api'
export const API_KEY = 'totally_random_api_key_here'

const FALLBACK_IMAGE = '/images/logos/ce-logo.png'

function apiHeaders(includeKey) {
  const headers = {
    Accept: 'application/json'
  }

  if (includeKey) {
    headers['Content-Type'] = 'application/json'
    headers['X-API-Key'] = API_KEY
  }

  return headers
}

function pluralKey(resource) {
  if (resource === 'property') return 'properties'
  return resource + 's'
}

function parsePayload(payload) {
  if (!payload) return {}
  if (typeof payload === 'object') return payload

  try {
    return JSON.parse(payload)
  } catch (error) {
    return {}
  }
}

export function getRecordPayload(record) {
  return parsePayload(record && record.payload)
}

export function getPropertyListing(record) {
  const payload = getRecordPayload(record)
  const data = payload.data || {}
  const rows = Array.isArray(data.listing) ? data.listing : []
  return rows[0] || {}
}

export function getAgentData(record) {
  const payload = getRecordPayload(record)
  const data = payload.data || {}
  const rows = Array.isArray(data.agent) ? data.agent : []
  return rows[0] || {}
}

export function getDevelopmentData(record) {
  const payload = getRecordPayload(record)
  const data = payload.data || {}
  const rows = Array.isArray(data.development) ? data.development : []
  return rows[0] || {}
}

export function canonicalWebRef(value) {
  const raw = value === null || value === undefined ? '' : String(value).trim()
  const digits = raw.match(/\d+/g)
  return digits && digits.length ? 'PALE-' + digits.join('') : ''
}

export function webRefNumber(value) {
  const canonical = canonicalWebRef(value)
  return canonical ? canonical.substring(5) : ''
}

export function makeWebRef(value) {
  const digits = String(value === null || value === undefined ? '' : value).replace(/\D/g, '')
  return digits ? 'PALE-' + digits : ''
}

export function nextWebRef(records) {
  const max = (records || []).reduce(function (highest, record) {
    const listing = getPropertyListing(record)
    const value = parseInt(webRefNumber(listing.web_ref || listing.own_ref_number), 10)
    return Number.isNaN(value) ? highest : Math.max(highest, value)
  }, 0)

  return 'PALE-' + (max + 1)
}

export function normaliseInternalId(value) {
  const id = Number(value)
  return Number.isInteger(id) && id > 0 ? id : null
}

function normaliseImage(image) {
  if (typeof image === 'string') return image.trim()
  if (!image || typeof image !== 'object') return ''
  return image.url || image.image_url || image.src || image.path || ''
}

function normaliseImages(payload, listing) {
  const images = Array.isArray(listing.listing_images)
    ? listing.listing_images.map(normaliseImage).filter(Boolean)
    : []

  const featured = normaliseImage(payload.featured_image || listing.featured_image)
  if (featured && images.indexOf(featured) === -1) images.unshift(featured)
  if (!images.length) images.push(FALLBACK_IMAGE)
  return images
}

function publicListingType(payload, listing) {
  const managerType = String(payload.manager_type || '').toLowerCase()
  if (managerType.indexOf('rental') !== -1) return 'To Let'
  if (managerType.indexOf('sale') !== -1) return 'For Sale'

  const current = String(listing.listing_type || '').toLowerCase()
  if (current.indexOf('rental') !== -1 || current === 'to let') return 'To Let'
  if (current.indexOf('sale') !== -1 || current === 'for sale') return 'For Sale'
  return listing.listing_type || 'For Sale'
}

function isCommercialRecord(record) {
  const payload = getRecordPayload(record)
  const tags = payload.tags || {}
  return String(payload.manager_type || '').toLowerCase().indexOf('commercial') === 0 || tags.commercial === true
}

function isDraftRecord(record) {
  const listing = getPropertyListing(record)
  return String(listing.status || 'Active').trim().toLowerCase() === 'draft'
}

export function normalisePublicListing(record) {
  const payload = getRecordPayload(record)
  const listing = getPropertyListing(record)
  const webRef = canonicalWebRef(listing.web_ref || listing.own_ref_number || record.ref_id)

  const normalised = Object.assign({}, listing, {
    web_ref: webRef,
    listing_type: publicListingType(payload, listing),
    listing_images: normaliseImages(payload, listing),
    price: Number(listing.price || 0),
    land_size: Number(listing.land_size || 0),
    measurement_type: listing.measurement_type || 'm²',
    building_size: Number(listing.building_size || 0),
    building_measurement_type: listing.building_measurement_type || listing.measurement_type || 'm²',
    lounges: Number(listing.lounges || 0),
    bedrooms: Number(listing.bedrooms || 0),
    bathrooms: Number(listing.bathrooms || 0),
    garages: Number(listing.garages || 0),
    parking: Number(listing.parking || 0),
    map_x_position: listing.map_x_position || null,
    map_y_position: listing.map_y_position || null,
    status: listing.status || 'Active'
  })

  delete normalised.id
  delete normalised.ref_id
  delete normalised.own_ref_number
  return normalised
}

export async function listManagedRecords(resource) {
  const response = await axios.get(API_BASE + '/' + pluralKey(resource) + '.php', {
    headers: apiHeaders(false)
  })
  const body = response.data || {}
  const key = pluralKey(resource)
  const records = body[key] || body.data || []
  return Array.isArray(records) ? records : []
}

export async function createManagedRecord(resource, payload) {
  const response = await axios.post(API_BASE + '/' + pluralKey(resource) + '.php', {
    payload: payload
  }, {
    headers: apiHeaders(true)
  })
  return response.data
}

export async function updateManagedRecord(resource, privateRefId, payload) {
  const response = await axios.put(API_BASE + '/' + pluralKey(resource) + '.php', {
    ref_id: privateRefId,
    payload: payload
  }, {
    headers: apiHeaders(true)
  })
  return response.data
}

export async function deleteManagedRecord(resource, privateRefId) {
  const response = await axios.delete(API_BASE + '/' + pluralKey(resource) + '.php', {
    headers: apiHeaders(true),
    data: {
      ref_id: privateRefId
    }
  })
  return response.data
}

export async function uploadPropertyImages(recordId, files, onProgress) {
  const formData = new FormData()
  formData.append('id', String(recordId))
  Array.prototype.forEach.call(files || [], function (file) {
    formData.append('images[]', file)
  })

  const response = await axios.post(API_BASE + '/upload_property_images.php', formData, {
    headers: {
      Accept: 'application/json',
      'X-API-Key': API_KEY
    },
    timeout: 120000,
    onUploadProgress: function (event) {
      if (!onProgress || !event.total) return
      onProgress(Math.round((event.loaded * 100) / event.total))
    }
  })

  const body = response.data || {}
  return Array.isArray(body.urls) ? body.urls : []
}

export async function deletePropertyImage(recordId, url) {
  const response = await axios.post(API_BASE + '/upload_property_images.php', {
    action: 'delete',
    id: recordId,
    url: url
  }, {
    headers: apiHeaders(true)
  })
  return response.data
}

export async function uploadAgentImage(recordId, file, previousUrl, onProgress) {
  const formData = new FormData()
  formData.append('id', String(recordId))
  formData.append('image', file)
  if (previousUrl) formData.append('previous_url', previousUrl)

  const response = await axios.post(API_BASE + '/upload_agent_image.php', formData, {
    headers: {
      Accept: 'application/json',
      'X-API-Key': API_KEY
    },
    timeout: 120000,
    onUploadProgress: function (event) {
      if (!onProgress || !event.total) return
      onProgress(Math.round((event.loaded * 100) / event.total))
    }
  })

  const body = response.data || {}
  return body.url || ''
}

export async function deleteAgentImage(recordId, url) {
  const response = await axios.post(API_BASE + '/upload_agent_image.php', {
    action: 'delete',
    id: recordId,
    url: url
  }, {
    headers: apiHeaders(true)
  })
  return response.data
}

export async function uploadDevelopmentImage(recordId, file, previousUrl, onProgress) {
  const formData = new FormData()
  formData.append('id', String(recordId))
  formData.append('image', file)
  if (previousUrl) formData.append('previous_url', previousUrl)

  const response = await axios.post(API_BASE + '/upload_development_image.php', formData, {
    headers: {
      Accept: 'application/json',
      'X-API-Key': API_KEY
    },
    timeout: 120000,
    onUploadProgress: function (event) {
      if (!onProgress || !event.total) return
      onProgress(Math.round((event.loaded * 100) / event.total))
    }
  })

  const body = response.data || {}
  return body.url || ''
}

export async function deleteDevelopmentImage(recordId, url) {
  const response = await axios.post(API_BASE + '/upload_development_image.php', {
    action: 'delete',
    id: recordId,
    url: url
  }, {
    headers: apiHeaders(true)
  })
  return response.data
}

export async function fetchPublicPropertyRecords() {
  return listManagedRecords('property')
}

export async function fetchPublicResidentialListings() {
  const records = await fetchPublicPropertyRecords()
  return records
    .filter(function (record) { return !isDraftRecord(record) && !isCommercialRecord(record) })
    .map(function (record) { return [normalisePublicListing(record)] })
}

export async function fetchPublicCommercialListings() {
  const records = await fetchPublicPropertyRecords()
  return records
    .filter(function (record) { return !isDraftRecord(record) && isCommercialRecord(record) })
    .map(function (record) { return [normalisePublicListing(record)] })
}

export async function fetchPublicFeaturedListings() {
  const records = await fetchPublicPropertyRecords()
  return records.filter(function (record) {
    return !isDraftRecord(record)
  }).map(function (record) {
    const payload = getRecordPayload(record)
    const listing = normalisePublicListing(record)
    return {
      payload: {
        listing: Object.assign({}, payload, {
          data: { listing: [listing] }
        })
      }
    }
  })
}

export async function fetchPublicPropertyByWebRef(webRef) {
  const wanted = canonicalWebRef(webRef)
  const records = await fetchPublicPropertyRecords()
  const record = records.find(function (candidate) {
    if (isDraftRecord(candidate)) return false
    const listing = getPropertyListing(candidate)
    return canonicalWebRef(listing.web_ref || listing.own_ref_number || candidate.ref_id) === wanted
  })

  if (!record) throw new Error('Property not found.')

  return {
    record: record,
    payload: getRecordPayload(record),
    listing: normalisePublicListing(record)
  }
}

export async function fetchPublicAgent(reference) {
  if (!reference) return {}
  const records = await listManagedRecords('agent')
  const wanted = String(reference).toLowerCase()
  const record = records.find(function (candidate) {
    const agent = getAgentData(candidate)
    const values = [candidate.ref_id, agent.ref_id, agent.email_address]
    return values.some(function (value) {
      return value !== null && value !== undefined && String(value).toLowerCase() === wanted
    })
  })
  return record ? getAgentData(record) : {}
}

export function apiErrorMessage(error, fallback) {
  if (error && error.response && error.response.data && error.response.data.message) {
    return error.response.data.message
  }
  if (error && error.message === 'Network Error' && fallback) return fallback
  if (error && error.message) return error.message
  return fallback || 'Something went wrong.'
}
